/** County row in the who-were-reaching snapshot. */
export interface WhoWereReachingCountySnapshot {
  name: string;
  memberCount: number;
}

/** Snapshot JSON built from BigQuery demographic county metrics. */
export const WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE = 'warehouse' as const;

/**
 * Coerces a raw county row member count to a finite number.
 * @param value - Warehouse or JSON value.
 * @returns Parsed count or 0.
 */
export const coerceWhoWereReachingMemberCount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Normalizes county rows from snapshot JSON (handles string counts from JSONB).
 * @param raw - `counties` array from snapshot.
 * @returns Valid county rows with numeric member counts.
 */
export const coerceWhoWereReachingCounties = (
  raw: unknown,
): WhoWereReachingCountySnapshot[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as { name?: unknown; memberCount?: unknown };
      const name = typeof record.name === 'string' ? record.name.trim() : '';
      const memberCount = coerceWhoWereReachingMemberCount(record.memberCount);
      if (!name || memberCount <= 0) {
        return null;
      }
      return { name, memberCount };
    })
    .filter((row): row is WhoWereReachingCountySnapshot => row !== null);
};

const countyNameKey = (name: string): string =>
  name.replace(/\s*County$/i, '').trim().toLowerCase();

/*
 * Matching on county names alone cannot work: the generator's palette is the
 * six largest Michigan counties, which is also the real top six for a Michigan
 * customer. Only the generated counts distinguish them, and the generator is
 * seeded by customer id + period, so the caller can regenerate the exact rows
 * a mock snapshot would hold and compare against them.
 */
/**
 * Detects county rows that are the deterministic mock output for this report.
 * @param counties - County rows from the snapshot.
 * @param generatedMockCounties - Mock rows for the same customer and period.
 * @returns Whether the snapshot counties are generated mock data.
 */
export const isGeneratedMockWhoWereReachingCounties = (
  counties: WhoWereReachingCountySnapshot[],
  generatedMockCounties: WhoWereReachingCountySnapshot[],
): boolean => {
  if (
    counties.length === 0 ||
    counties.length !== generatedMockCounties.length
  ) {
    return false;
  }
  return counties.every((row, index) => {
    const mockRow = generatedMockCounties[index];
    return (
      countyNameKey(row.name) === countyNameKey(mockRow.name) &&
      row.memberCount === mockRow.memberCount
    );
  });
};

/**
 * Returns true when the snapshot was built from warehouse demographic metrics.
 * @param dataSnapshot - Section snapshot JSON.
 * @returns Whether county counts should be treated as authoritative.
 */
export const isWarehouseWhoWereReachingSnapshot = (
  dataSnapshot: Record<string, unknown>,
): boolean =>
  dataSnapshot.snapshotSource === WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE;
